import { useState, useEffect, useRef } from 'react';
import { createForm } from 'rc-form';

import { Input, Box, Text, Spacer, Select, Button, Notify } from '@flexisaf/flexibull2';
import { lighten } from 'polished';
import { Theme } from '../../config/theme';

import useValidateSchool from '../../hooks/useValidateSchool';
import httpClient from '../../utils/http-client';

let format = /[ `§±!@#$%^&*()+\-=\[\]{};':"\\|,.<>\/?~]|\s$/;

export const useDebounce = (func, delay) => {
  const debounceTimer = useRef(null);

  const debouncedFunc = (...args) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current); // Clear the previous timer if it exists
    }

    debounceTimer.current = setTimeout(() => {
      func(...args); // Call the function after the delay
    }, delay);
  };

  return debouncedFunc;
};


const Step1 = ({ form, nextStep, data, isMadrasa }) => {
  const { getFieldError, getFieldProps, getFieldValue, validateFields } = form;
  const [countries, setCountries] = useState([]);
  const [urls, setUrls] = useState([]);
  const [schoolUrl, setSchoolUrl] = useState('');
  const [error, setError] = useState(false); // Track validation errors for the short name

  // Use the custom debounce hook
  const debouncedNotify = useDebounce((value) => Notify(value, { status: 'info' }), 5000);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await httpClient.get(`/countries`);
        setCountries(response.data);
      } catch (error) {
        // setError(error);
        return;
      }
    };
    fetchCountries();
  }, []);

  useEffect(() => {
    const fetchUrlPrefixes = async () => {
      try {
        const response = await httpClient.get(`/signup/url-prefixes`);
        setUrls(response.data.url_prefixes);
      } catch (error) {
        return;
      }
    };
    fetchUrlPrefixes();
  }, []);

  const { schoolExists, setSchoolExists, validateSchool } = useValidateSchool({
    tenantId: schoolUrl
  });

  const validateUrl = async (e) => {
    e.stopPropagation();
    setSchoolUrl(e.target.value);
    await validateSchool(getFieldValue('url_prefix'));
  };

  useEffect(() => {
    if (data?.url_prefix) {
      validateSchool(data?.url_prefix);
    }
  }, [data?.url_prefix, validateSchool])


 // Handles validation logic for the short name field
const handleUrl = (e) => {
  if (!e) {
    setError(false); // Clear error state if the input is empty
    return;
  }

  if (
    urls.length > 0 &&
    urls.find((url) => url.toLowerCase() === getFieldValue("short_name"))
  ) {
    Notify("This school short name already exists", { status: "info" });
  } else if (format.test(e)) {
    debouncedNotify(
      "School short name must contain only numbers, letters and underscores",
    );
    setError(true);
  } else {
    setSchoolExists(false);
    setError(false);
    clearTimeout(debouncedNotify);
  }

};

  const url_prefix = getFieldValue('url_prefix')?.toLowerCase() ?? '';

  const onSubmit = async () => {
    validateFields((err, values) => {
      if (!err) {
        if (schoolExists) {
          return Notify('School Short name already exists', {
            status: 'info'
          });
        }
        nextStep({ ...values, url_prefix: values?.url_prefix?.toLowerCase() })
      }
    })
  }

  // Validation error message for the short name input field
  const urlPrefixError = getFieldError("url_prefix")
    ? schoolExists
      ? "School short name already exists"
      : "School short name is required"
    : format.test(getFieldValue("url_prefix"))
    ? "School short name must contain only letters, numbers, and underscores"
    : "";

  const schoolNameDefinition = isMadrasa ? 'Madrasa' : 'School'

  return (
    <Box pad="10px 0"><Box
      margin="10px 0"
      pad="15px"
      style={{
        display: 'flex',
        gap: '10px',
        borderRadius: Theme.PrimaryRadius,
        background: '#f0f0f0',
        alignItems: 'center',
      }}
    >
      <i className="saf-magic-star" style={{ color: 'gold', fontSize: '16px' }} />
      <Box>
        <Text size="14px" bold>
          What is Short Name?
        </Text>
        <br />
        <Text size="11px">
          Short name is an abbreviation of your {schoolNameDefinition} name used as a unqiue identifier for parents, teachers & students to access your platform.
        </Text>
      </Box>
    </Box>
      <Box pad="10px 0" align="left">
        <Text bold>About the {schoolNameDefinition}</Text>
        <Spacer />
        <Text>A bit of information about the {schoolNameDefinition} you work and represent. </Text>
      </Box>
      <Box pad="40px 0 10px 0">
        <Input label={`${schoolNameDefinition} Name`} placeholder={`Enter ${schoolNameDefinition} Name...`} required block
          error={getFieldError('school_name') ? 'School name is required' : null}
          {...getFieldProps('school_name', {
            initialValue: data ? data?.school_name : '',
            rules: [{ required: true }]
          })} />
      </Box>
      <Box pad="40px 0 5px 0">
        <Input
          label={`${schoolNameDefinition} Short Name`}
          placeholder={`Enter ${schoolNameDefinition} Short Name...`}
          required
          block
          error={urlPrefixError} // Display appropriate error messages
          {...getFieldProps('url_prefix', {
            initialValue: data ? data?.url_prefix : '',
            onChange: (e) => handleUrl(e?.target?.value?.trim()), // Using debounced handler
            rules: [{ required: true }]
          })}
          onBlur={(e) => validateUrl(e)}
        />
      </Box>
      <Box
        pad="10px"
        style={{ borderRadius: Theme.borderRadius, background: lighten(0.45, Theme.PrimaryColor) }}
      >
        <Text
          block
          color={Theme.PrimaryColor}
          style={{ borderBottom: `0.5px solid ${Theme.PrimaryColor}`, padding: '0 0 10px 0' }}
        >
          {' '}
          <span style={{ paddiing: '0 5px 0 0 ' }}>https://</span>{' '}
          <span>
            <span style={{ fontWeight: 'bold' }}>{url_prefix}</span>.safsims.com
          </span>
        </Text>
        <Spacer />
        <Text>This will be the main URL to the portal</Text>
      </Box>
      <Box pad="20px 0 10px 0">
        <Select
          placeholder="Select Country"
          elevate={true}
          name="country"
          label="Country"
          spaceTop
          spaceBottom="10px"
          required
          options={countries.map((country) => {
            return {
              label: country.name,
              value: country.country_code,
              iso3: country.iso3
            };
          })}
          style={{
            width: '100%',
            fontSize: '1em'
          }}
          error={getFieldError('country') ? 'Country is required' : null}
          {...getFieldProps('country', {
            initialValue: data ? data?.country : '',
            rules: [{ required: true }],
          })}
        />
      </Box>
      <Box pad="20px 0 10px 0">
        <Input
          label={`${schoolNameDefinition} Website`}
          placeholder="www.startopschool.com"
          block
          {...getFieldProps('school_website', {
            initialValue: data ? data?.school_website : '',
            rules: []
          })}
        />
      </Box>
      {/* Button is disabled if there's an error in the short name field */}
      <Box align="center">
        <Button 
        disabled = {urlPrefixError}
        onClick={() => onSubmit()}>Next</Button>
      </Box>
    </Box>
  );
};

export default createForm()(Step1);
